import { combineEpics } from 'redux-observable';
import { concatMap, filter, forkJoin, from, map, withLatestFrom } from 'rxjs';

import cartModule, { CartModel } from 'domains/cart';

import {
  createCartOrderOption,
  deleteCartOrderOption,
  getCartOrderOptions,
} from './cart_order_options.actions';
import { CartOrderOptionsModel } from './cart_order_options.model';

import { catchAndLogError } from 'utils/common';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

/**
 * After fetching the cart, if there are cart order options, we should fetch those
 * to get the pricing data
 */
const getCartOrderOptionsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(cartModule.actions.get.fulfilled.match),
    filter(({ payload }) => payload.options.length > 0),
    concatMap(() =>
      from(CartOrderOptionsModel.all()).pipe(
        map(getCartOrderOptions.fulfilled),
        catchAndLogError(getCartOrderOptions.rejected())
      )
    )
  );

const createCartOrderOptionEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(createCartOrderOption.pending.match),
    withLatestFrom(state$),
    map(([action, state]) => ({
      add: action.payload,
      remove: state.cart.betterCart?.options[0]?.id,
    })),
    concatMap((payload) =>
      from(CartOrderOptionsModel.create(payload)).pipe(
        concatMap(() => forkJoin([CartModel.get(), CartOrderOptionsModel.all()])),
        map(([cart, coos]) => createCartOrderOption.fulfilled({ ...cart, options: coos })),
        catchAndLogError(createCartOrderOption.rejected())
      )
    )
  );

const deleteCartOrderOptionEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(deleteCartOrderOption.pending.match),
    concatMap(({ payload }) =>
      from(CartOrderOptionsModel.delete(payload)).pipe(
        concatMap(() => CartModel.get()),
        map((cart) => deleteCartOrderOption.fulfilled(cart)),
        catchAndLogError(deleteCartOrderOption.rejected())
      )
    )
  );

const combined = combineEpics(
  getCartOrderOptionsEpic,
  createCartOrderOptionEpic,
  deleteCartOrderOptionEpic
);
export default combined;
